import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { notification, Skeleton } from "antd";
import { EmailDetail } from "../../components/mail/EmailDetail";
import mailClientAPI from "../../api/mail-client-api";
import { useUser } from "../../context/UserContext";

function EmailDetailView() {
  const { path, emailId } = useParams();
  const navigate = useNavigate();
  const { mailboxes, loading: mailboxesLoading, labels = [], labelsLoading = false, refreshLabels, updateMailboxCounts } = useOutletContext() || {};
  const { userInfo } = useUser();
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTrustBar, setShowTrustBar] = useState(false);
  const [imagesAllowed, setImagesAllowed] = useState(false);
  // Track the current mailbox path and email ID for which we're expecting a response
  // This prevents race conditions when switching mailboxes/emails quickly
  const currentRequestRef = useRef(null);

  const mailboxPath = path ? decodeURIComponent(path) : "INBOX";
  const userEmail = userInfo?.email || "";
  const emailSubject = email?.subject || "(No Subject)";

  // Load email when component mounts or params change
  // Don't wait for mailboxes to load - we can load the email directly
  useEffect(() => {
    const loadEmail = async () => {
      if (!emailId) {
        setError("Email ID is required");
        setLoading(false);
        return;
      }

      // Set the current request identifier we're loading
      const expectedMailboxPath = mailboxPath;
      const expectedEmailId = emailId;
      const requestId = `${expectedMailboxPath}:${expectedEmailId}`;
      currentRequestRef.current = requestId;
      
      setLoading(true);
      setError(null);
      try {
        const response = await mailClientAPI.getEmail(expectedMailboxPath, expectedEmailId);
        
        // Only update state if this response is for the current request
        // This prevents stale responses from overwriting newer data
        if (currentRequestRef.current === requestId) {
          const emailData = response.Output;
          
          // Transform to match expected format
          const transformedEmail = {
            ...emailData,
            id: emailData.id || expectedEmailId,
            timestamp: emailData.timestamp ? new Date(emailData.timestamp) : new Date(emailData.date || Date.now()),
            folder: expectedMailboxPath.toLowerCase(),
            labels: emailData.labels || [],
          };

          console.log("transformedEmail", transformedEmail);
          
          setEmail(transformedEmail);

          // Check if images should be blocked
          const isTrusted = transformedEmail.isTrusted === true;
          setImagesAllowed(isTrusted);
          
          // Check if email has images (basic check for img tags or background images)
          const hasImages = transformedEmail.body && (
            transformedEmail.body.includes('<img') || 
            transformedEmail.body.includes('background-image') ||
            transformedEmail.body.includes('url(')
          );
          
          // Show trust bar if not trusted and has HTML content with images
          setShowTrustBar(!isTrusted && transformedEmail.bodyIsHtml && hasImages);

          // Mark email as read when it loads (but not for Scheduled folder)
          if (!emailData.isRead && expectedMailboxPath !== "Scheduled") {
            try {
              await mailClientAPI.changeFlags(expectedMailboxPath, expectedEmailId, ["\\Seen"], []);
              // Only update local state if this is still the current request
              if (currentRequestRef.current === requestId) {
                setEmail(prev => prev ? { ...prev, isRead: true } : null);
                
                // Update mailbox counts (email was unread, now marked as read)
                if (updateMailboxCounts) {
                  console.log("updateMailboxCounts", updateMailboxCounts);
                  const sourceFolder = transformedEmail.folder || expectedMailboxPath;
                  updateMailboxCounts({
                    sourceFolder: sourceFolder,
                    readStatusChange: 'read',
                    wasUnread: false,
                    count: 1,
                  });
                }
              }
            } catch (markReadErr) {
              // Don't fail the whole request if marking as read fails
              console.error("Error marking email as read:", markReadErr);
            }
          }
        } else {
          console.log("Ignoring stale response for email:", requestId);
        }
      } catch (err) {
        // Only update state on error if this is still the current request
        if (currentRequestRef.current === requestId) {
          console.error("Error loading email:", err);
          setError(err.response?.data?.Output?.message || err.message || "Failed to load email");
        }
      } finally {
        // Only update loading state if this is still the current request
        if (currentRequestRef.current === requestId) {
          setLoading(false);
        }
      }
    };

    // Load email immediately without waiting for mailboxes
    // Do not have updateMailboxCounts as a dependency here, it will cause a loop
    loadEmail();
  }, [mailboxPath, emailId]);

  const handleBack = () => {
    navigate(`/mailboxes/${encodeURIComponent(mailboxPath)}`);
  };

  // Show loading only when loading the email itself, not when loading mailboxes
  if (loading) {
    return (
      <>
        <Helmet>
          <title>Loading... - {userEmail || "Grigora Mail"}</title>
        </Helmet>
        <div className="flex flex-col h-full bg-white">
        {/* Toolbar Skeleton */}
        <div className="flex items-center px-4 py-2 border-b border-gray-100 bg-white sticky top-0 z-10">
          <Skeleton.Button active size="small" style={{ width: 40, height: 40, marginRight: 16 }} />
          <div className="flex space-x-1">
            <Skeleton.Button active size="small" style={{ width: 40, height: 40 }} />
            <Skeleton.Button active size="small" style={{ width: 40, height: 40 }} />
            <Skeleton.Button active size="small" style={{ width: 40, height: 40 }} />
          </div>
          <div className="flex-1"></div>
          <div className="flex space-x-1">
            <Skeleton.Button active size="small" style={{ width: 40, height: 40 }} />
            <Skeleton.Button active size="small" style={{ width: 40, height: 40 }} />
          </div>
        </div>

        {/* Content Skeleton */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Header Section Skeleton */}
          <div className="mb-8 border-b border-gray-100 pb-6">
            <div className="flex justify-between items-start mb-6 gap-4">
              <div className="flex-1 flex items-start gap-1">
                <Skeleton.Input active size="large" style={{ width: "70%", height: 32, marginBottom: 8 }} />
                <Skeleton.Input active size="small" style={{ width: "50%", height: 20 }} />
              </div>
              <div className="flex items-center space-x-1">
                <Skeleton.Button active size="small" style={{ width: 40, height: 40 }} />
                <Skeleton.Button active size="small" style={{ width: 40, height: 40 }} />
                <Skeleton.Button active size="small" style={{ width: 40, height: 40 }} />
              </div>
            </div>

            <div className="flex items-start justify-between relative">
              <div className="flex items-start flex-1">
                <Skeleton.Avatar active size={40} style={{ marginRight: 12, marginTop: 4 }} />
                <div className="flex-1 min-w-0 flex items-start gap-1">
                  <Skeleton.Input active size="small" style={{ width: 200, height: 20, marginBottom: 8 }} />
                  <Skeleton.Input active size="small" style={{ width: 150, height: 16 }} />
                </div>
              </div>
              <Skeleton.Input active size="small" style={{ width: 120, height: 16, marginTop: 4 }} />
            </div>
          </div>

          {/* Email Body Skeleton */}
          <div className="space-y-4">
            <Skeleton active paragraph={{ rows: 4 }} />
            <Skeleton active paragraph={{ rows: 3 }} />
            <Skeleton active paragraph={{ rows: 5 }} />
          </div>
        </div>
      </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>Error - {userEmail || "Grigora Mail"}</title>
        </Helmet>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <span className="material-icons-outlined text-6xl text-gray-400 mb-4">error_outline</span>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={handleBack}
              className="px-4 py-2 bg-grigora-500 text-white rounded-lg hover:bg-grigora-600"
            >
              Go Back
            </button>
          </div>
        </div>
      </>
    );
  }

  if (!email) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Email not found</p>
          <button
            onClick={handleBack}
            className="mt-4 px-4 py-2 bg-grigora-500 text-white rounded-lg hover:bg-grigora-600"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const handleFlagChange = async (addFlags = [], removeFlags = []) => {
    try {
      // Check if read status is changing
      const wasUnreadBefore = email ? !email.isRead : false;
      let isUnreadAfter = wasUnreadBefore;
      
      if (addFlags.includes("\\Seen")) {
        isUnreadAfter = false;
      }
      if (removeFlags.includes("\\Seen")) {
        isUnreadAfter = true;
      }
      
      // Update local state based on flag changes
      setEmail(prev => {
        if (!prev) return null;
        const updated = { ...prev };
        if (addFlags.includes("\\Seen")) updated.isRead = true;
        if (removeFlags.includes("\\Seen")) updated.isRead = false;
        if (addFlags.includes("\\Flagged")) updated.isStarred = true;
        if (removeFlags.includes("\\Flagged")) updated.isStarred = false;
        return updated;
      });
      
      await mailClientAPI.changeFlags(mailboxPath, emailId, addFlags, removeFlags);
      
      // Update mailbox counts if read status changed
      if (updateMailboxCounts && wasUnreadBefore !== isUnreadAfter) {
        const sourceFolder = email?.folder || mailboxPath;
        if (isUnreadAfter) {
          // Changed to unread
          updateMailboxCounts({
            sourceFolder: sourceFolder,
            readStatusChange: 'unread',
            wasUnread: false,
            count: 1,
          });
        } else {
          // Changed to read
          updateMailboxCounts({
            sourceFolder: sourceFolder,
            readStatusChange: 'read',
            wasUnread: true,
            count: 1,
          });
        }
      }
    } catch (err) {
      console.error("Error changing flags:", err);
      notification.error({
        message: "Failed to update email",
        duration: 3,
      });
    }
  };

  const handleArchive = async () => {
    try {
      const wasUnread = email ? !email.isRead : false;
      const sourceFolder = email?.folder || mailboxPath;
      
      await mailClientAPI.archiveEmail(mailboxPath, emailId);
      
      // Update mailbox counts
      if (updateMailboxCounts) {
        updateMailboxCounts({
          sourceFolder: sourceFolder,
          destinationFolder: "Archive",
          wasUnread: wasUnread,
          count: 1,
        });
      }
      
      notification.success({
        message: "Email archived",
        duration: 3,
      });
      // Navigate back to mailbox after archiving
      handleBack();
    } catch (error) {
      console.error("Error archiving email:", error);
      notification.error({
        message: "Failed to archive email",
        duration: 3,
      });
    }
  };

  const handleSpam = async () => {
    try {
      const wasUnread = email ? !email.isRead : false;
      const sourceFolder = email?.folder || mailboxPath;
      
      await mailClientAPI.markAsSpam(mailboxPath, emailId);
      
      // Update mailbox counts (spam typically goes to Junk/Spam folder)
      if (updateMailboxCounts) {
        updateMailboxCounts({
          sourceFolder: sourceFolder,
          destinationFolder: "Junk",
          wasUnread: wasUnread,
          count: 1,
        });
      }
      
      notification.success({
        message: "Email marked as spam",
        duration: 3,
      });
      // Navigate back to mailbox after marking as spam
      handleBack();
    } catch (error) {
      console.error("Error marking email as spam:", error);
      notification.error({
        message: "Failed to mark email as spam",
        duration: 3,
      });
    }
  };

  const handleDelete = async () => {
    try {
      const wasUnread = email ? !email.isRead : false;
      const sourceFolder = email?.folder || mailboxPath;
      
      await mailClientAPI.trashEmail(mailboxPath, emailId);
      
      // Update mailbox counts
      if (updateMailboxCounts) {
        updateMailboxCounts({
          sourceFolder: sourceFolder,
          destinationFolder: "Trash",
          wasUnread: wasUnread,
          count: 1,
        });
      }
      
      notification.success({
        message: "Email deleted",
        duration: 3,
      });
      // Navigate back to mailbox after deleting
      handleBack();
    } catch (error) {
      console.error("Error deleting email:", error);
      notification.error({
        message: "Failed to delete email",
        duration: 3,
      });
    }
  };

  const handleMarkAsUnread = async () => {
    try {
      // Only call API if email is read
      if (email?.isRead) {
        const wasRead = true;
        const sourceFolder = email?.folder || mailboxPath;
        
        // Update local state
        setEmail(prev => prev ? { ...prev, isRead: false } : null);
        await mailClientAPI.markAsUnread(mailboxPath, emailId);
        
        // Update mailbox counts (only if it was read)
        if (updateMailboxCounts && wasRead) {
          updateMailboxCounts({
            sourceFolder: sourceFolder,
            readStatusChange: 'unread',
            wasUnread: false, // was read, now unread
            count: 1,
          });
        }
        
        notification.success({
          message: "Marked as unread",
          duration: 3,
        });
      }
    } catch (error) {
      console.error("Error marking email as unread:", error);
      notification.error({
        message: "Failed to mark email as unread",
        duration: 3,
      });
    }
  };

  const handleStar = async () => {
    try {
      if (email?.isStarred) {
        // Update local state
        setEmail(prev => {
          if (!prev) return null;
          return { ...prev, isStarred: false };
        });
        await mailClientAPI.changeFlags(mailboxPath, emailId, [], ["\\Flagged"]);
        notification.success({
          message: "Star removed",
          duration: 3,
        });
      } else {
        // Update local state
        setEmail(prev => {
          if (!prev) return null;
          return { ...prev, isStarred: true };
        });
        await mailClientAPI.changeFlags(mailboxPath, emailId, ["\\Flagged"], []);
        notification.success({
          message: "Starred",
          duration: 3,
        });
      }
    } catch (error) {
      console.error("Error toggling star:", error);
      notification.error({
        message: "Failed to update star",
        duration: 3,
      });
      // Revert local state on error
      setEmail(prev => {
        if (!prev) return null;
        return { ...prev, isStarred: !prev.isStarred };
      });
    }
  };

  const handleAllowImages = () => {
    // Just show images for this email without making API call
    setImagesAllowed(true);
    setShowTrustBar(false);
  };

  const handleAlwaysAllowImages = async () => {
    try {
      const senderEmail = email?.senderEmail || email?.senderName;
      if (!senderEmail) {
        console.error("Cannot trust sender: sender email not found");
        notification.error({
          message: "Cannot trust sender: sender email not found",
          duration: 3,
        });
        return;
      }
      await mailClientAPI.trustSender(mailboxPath, emailId, senderEmail);
      setImagesAllowed(true);
      setShowTrustBar(false);
      // Update email to reflect trust status
      setEmail(prev => prev ? { ...prev, isTrusted: true } : null);
      notification.success({
        message: "Sender trusted - images will always load",
        duration: 3,
      });
    } catch (error) {
      console.error("Error trusting sender:", error);
      notification.error({
        message: "Failed to trust sender",
        duration: 3,
      });
    }
  };

  const handleReply = async (replyData) => {
    try {
      const { to, cc, bcc, subject, body, attachments = [] } = replyData;

      // Prepare form data for sending
      const formData = new FormData();
      formData.append("to", to);
      if (cc) formData.append("cc", cc);
      if (bcc) formData.append("bcc", bcc);
      formData.append("subject", subject || "(No Subject)");
      formData.append("body", body);
      formData.append("html", body);

      // Add attachments
      attachments.forEach((att) => {
        formData.append("attachments", att);
      });

      await mailClientAPI.sendEmail(formData);

      notification.success({
        message: "Reply sent successfully",
        description: `Your reply has been sent.`,
        duration: 3,
      });
    } catch (error) {
      console.error("Error sending reply:", error);
      notification.error({
        message: "Failed to send reply",
        description: error.response?.data?.Output?.message || error.message || "Failed to send reply",
        duration: 3,
      });
      throw error;
    }
  };

  return (
    <>
      <Helmet>
        <title>{emailSubject} - {userEmail || "Grigora Mail"}</title>
      </Helmet>
      <EmailDetail 
        email={email}
        onBack={handleBack}
        onFlagChange={handleFlagChange}
        mailboxPath={mailboxPath}
        onArchive={handleArchive}
        onSpam={handleSpam}
        onDelete={handleDelete}
        onMarkAsUnread={handleMarkAsUnread}
        onStar={handleStar}
        imagesAllowed={imagesAllowed}
        showTrustBar={showTrustBar}
        onAllowImages={handleAllowImages}
        onAlwaysAllowImages={handleAlwaysAllowImages}
        onReply={handleReply}
        userInfo={userInfo}
        labels={labels}
        labelsLoading={labelsLoading}
        refreshLabels={refreshLabels}
        onEmailUpdate={(updatedEmail) => {
          setEmail(updatedEmail);
        }}
      />
    </>
  );
}

export default EmailDetailView;

